import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { apiClient } from '../../utils/apiClient';
import { useAuth } from '../../contexts/AuthContext';
import {
    PlusCircle, Edit, Trash2, Loader2, AlertCircle, Info, Layers,
    ShieldAlert, Search, Briefcase, Clock, Sparkles, X
} from 'lucide-react';

// ====================================================================
//     Vardiya Grubu Formu Bileşeni (Geniş İnteraktif Modal)
// ====================================================================
const ShiftGroupForm = ({ currentGroup, onSave, onCancel, permissions }) => {
    const [formData, setFormData] = useState(
        currentGroup || { name: '', description: '', workShiftIds: [] }
    );
    const [availableShifts, setAvailableShifts] = useState([]);
    const [shiftSearch, setShiftSearch] = useState('');
    const [error, setError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        const fetchShifts = async () => {
            try {
                const data = await apiClient('scheduling/shifts', { method: 'GET' });
                setAvailableShifts(data || []);
            } catch {
                setError('Vardiyalar yüklenemedi.');
            }
        };
        fetchShifts();
    }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleShiftChange = (shiftId) => {
        setFormData(prev => {
            const newWorkShiftIds = prev.workShiftIds.includes(shiftId)
                ? prev.workShiftIds.filter(id => id !== shiftId)
                : [...prev.workShiftIds, shiftId];
            return { ...prev, workShiftIds: newWorkShiftIds };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const isNew = !formData.id;
        const requiredPermission = isNew ? 'shift_groups:create' : 'shift_groups:update';
        if (!permissions.includes(requiredPermission)) {
            setError('Bu işlemi yapma yetkiniz yok.');
            return;
        }

        setSubmitting(true);
        setError(null);
        try {
            const method = formData.id ? 'PUT' : 'POST';
            const url = formData.id ? `scheduling/groups/${formData.id}` : 'scheduling/groups';

            await apiClient(url, { method, body: formData });
            onSave();
        } catch (err) {
            setError(err.message || 'Bir hata oluştu.');
        } finally {
            setSubmitting(false);
        }
    };

    const filteredAvailableShifts = useMemo(() => {
        return availableShifts.filter(s =>
            !shiftSearch ||
            s.name?.toLowerCase().includes(shiftSearch.toLowerCase()) ||
            s.shortCode?.toLowerCase().includes(shiftSearch.toLowerCase())
        );
    }, [availableShifts, shiftSearch]);

    return (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl border border-gray-200 dark:border-gray-700 max-h-[90vh] flex flex-col overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center bg-white dark:bg-gray-800">
                    <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-purple-100 dark:bg-purple-900/40 text-purple-600 dark:text-purple-400">
                            <Layers size={22} />
                        </div>
                        <div>
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {formData.id ? 'Vardiya Grubunu Düzenle' : 'Yeni Vardiya Grubu Oluştur'}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                                Tekli veya çoklu (rotasyonlu) vardiya havuzu tanımlayın.
                            </p>
                        </div>
                    </div>
                    <button onClick={onCancel} className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 overflow-y-auto space-y-5">
                    {error && (
                        <div className="text-red-700 bg-red-50 dark:bg-red-900/30 p-3.5 rounded-xl border border-red-200 dark:border-red-800 text-sm flex items-center">
                            <AlertCircle size={18} className="mr-2 flex-shrink-0" /> {error}
                        </div>
                    )}

                    <div>
                        <label htmlFor="name" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Grup Adı</label>
                        <input
                            type="text"
                            name="name"
                            id="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                            placeholder="Örn: Fabrika 3'lü Üretim Vardiyaları, Merkez Ofis Grubu"
                            className="w-full px-3.5 py-2.5 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div>
                        <label htmlFor="description" className="block text-sm font-semibold text-gray-700 dark:text-gray-200 mb-1">Açıklama (Opsiyonel)</label>
                        <textarea
                            name="description"
                            id="description"
                            value={formData.description || ''}
                            onChange={handleChange}
                            rows="2"
                            placeholder="Bu vardiya grubunun hangi departman veya çalışma düzeni için kullanıldığını belirtin..."
                            className="w-full px-3.5 py-2 border border-gray-300 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:ring-2 focus:ring-purple-500"
                        />
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="block text-sm font-semibold text-gray-700 dark:text-gray-200">
                                Gruba Dahil Edilecek Vardiyalar
                            </label>
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-purple-100 text-purple-700 dark:bg-purple-900/50 dark:text-purple-300">
                                {formData.workShiftIds.length} Vardiya Seçili
                            </span>
                        </div>

                        <div className="p-3 mb-3 rounded-xl bg-purple-50/70 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 text-xs text-purple-900 dark:text-purple-200 flex items-start gap-2">
                            <Sparkles size={15} className="text-purple-600 flex-shrink-0 mt-0.5" />
                            <span>
                                <strong>Akıllı Vardiya Eşleştirme:</strong> Gruba birden fazla vardiya (Örn: 08:00-16:00, 16:00-24:00, 00:00-08:00) eklerseniz, puantaj motoru personelin turnikeden giriş yaptığı saate en yakın vardiyayı otomatik olarak seçer.
                            </span>
                        </div>

                        <div className="relative mb-2.5">
                            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Listede vardiya ara..."
                                value={shiftSearch}
                                onChange={(e) => setShiftSearch(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 text-xs border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 max-h-60 overflow-y-auto p-1">
                            {filteredAvailableShifts.map(shift => {
                                const selected = formData.workShiftIds.includes(shift.id);
                                return (
                                    <button
                                        key={shift.id}
                                        type="button"
                                        onClick={() => handleShiftChange(shift.id)}
                                        className={`p-3 rounded-xl border text-left transition-all flex items-center justify-between ${
                                            selected
                                                ? 'border-purple-600 bg-purple-50/80 dark:bg-purple-900/30 ring-2 ring-purple-500/20'
                                                : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5 min-w-0">
                                            <input
                                                type="checkbox"
                                                checked={selected}
                                                onChange={() => {}}
                                                className="h-4 w-4 text-purple-600 border-gray-300 rounded flex-shrink-0"
                                            />
                                            <div className="truncate">
                                                <div className="font-bold text-xs text-gray-900 dark:text-white truncate">{shift.name}</div>
                                                <div className="text-[11px] text-gray-500 dark:text-gray-400 font-mono">
                                                    {shift.startTime?.substring(0, 5)} - {shift.endTime?.substring(0, 5)}
                                                </div>
                                            </div>
                                        </div>
                                        {shift.shortCode && (
                                            <span className="ml-2 px-1.5 py-0.5 text-[10px] font-mono font-bold rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                                {shift.shortCode}
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                        <button
                            type="button"
                            onClick={onCancel}
                            disabled={submitting}
                            className="px-5 py-2.5 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 font-medium text-sm"
                        >
                            İptal
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-6 py-2.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold text-sm flex items-center shadow-lg shadow-purple-500/25"
                        >
                            {submitting && <Loader2 className="animate-spin mr-2" size={18} />}
                            {formData.id ? 'Grubu Güncelle' : 'Vardiya Grubunu Oluştur'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// ====================================================================
//     Ana Vardiya Grubu Yönetim Bileşeni
// ====================================================================
export default function ShiftGroupManagement({ onDataChange }) {
    const { isAuthenticated, permissions } = useAuth();
    const [shiftGroups, setShiftGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentGroup, setCurrentGroup] = useState(null);
    const [deleteConfirmData, setDeleteConfirmData] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');

    const canView = useMemo(() => permissions.includes('shift_groups:view_list'), [permissions]);
    const canCreate = useMemo(() => permissions.includes('shift_groups:create'), [permissions]);
    const canUpdate = useMemo(() => permissions.includes('shift_groups:update'), [permissions]);
    const canDelete = useMemo(() => permissions.includes('shift_groups:delete'), [permissions]);

    const fetchShiftGroups = useCallback(async () => {
        if (!canView) {
            setError("Vardiya gruplarını görüntüleme yetkiniz yok.");
            setLoading(false);
            return;
        }
        if (!isAuthenticated) return;
        setLoading(true);
        setError(null);
        try {
            const data = await apiClient('scheduling/groups', { method: 'GET' });
            setShiftGroups(data || []);
            if (onDataChange) onDataChange();
        } catch (err) {
            setError(err.message || 'Vardiya grupları yüklenirken bir hata oluştu.');
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, canView, onDataChange]);

    useEffect(() => {
        fetchShiftGroups();
    }, [fetchShiftGroups]);

    const handleOpenModal = (group = null) => {
        const requiredPermission = group ? 'shift_groups:update' : 'shift_groups:create';
        if (!permissions.includes(requiredPermission)) {
            alert("Bu işlemi yapma yetkiniz bulunmamaktadır.");
            return;
        }
        const groupData = group ? { ...group, workShiftIds: (group.workShifts || []).map(s => s.id) } : null;
        setCurrentGroup(groupData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => setIsModalOpen(false);
    const handleSaveSuccess = () => { handleCloseModal(); fetchShiftGroups(); };

    const handleDeleteClick = (id) => {
        if (!canDelete) {
            setDeleteConfirmData({
                type: 'block',
                message: "Vardiya grubu silme yetkiniz bulunmamaktadır."
            });
            return;
        }
        setDeleteConfirmData({
            id,
            type: 'initial',
            message: "Bu vardiya grubunu silmek istediğinizden emin misiniz?"
        });
    };

    const handleDeleteConfirm = async (id, force = false) => {
        try {
            await apiClient(`scheduling/groups/${id}${force ? '?force=true' : ''}`, { method: 'DELETE' });
            setDeleteConfirmData(null);
            fetchShiftGroups();
        } catch (err) {
            if (err.status === 409 && err.payload?.requiresConfirmation) {
                setDeleteConfirmData({
                    id,
                    type: 'conflict',
                    message: err.payload.message,
                    usedIn: err.payload.usedIn || []
                });
            } else {
                setDeleteConfirmData({
                    type: 'block',
                    message: err.message || 'Vardiya grubu silinirken bir hata oluştu.'
                });
            }
        }
    };

    const filteredGroups = useMemo(() => {
        return shiftGroups.filter(g =>
            !searchQuery ||
            g.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
            g.description?.toLowerCase().includes(searchQuery.toLowerCase())
        );
    }, [shiftGroups, searchQuery]);

    const stats = useMemo(() => ({
        total: shiftGroups.length,
        singleShift: shiftGroups.filter(g => (g.workShifts || []).length === 1).length,
        multiShift: shiftGroups.filter(g => (g.workShifts || []).length > 1).length
    }), [shiftGroups]);

    if (!loading && !canView) {
        return (
            <div className="p-8 bg-red-50 dark:bg-red-900/10 rounded-2xl border border-red-200 dark:border-red-800 text-center">
                <ShieldAlert className="mx-auto h-12 w-12 text-red-400" />
                <h3 className="mt-2 text-lg font-bold text-red-800 dark:text-red-300">Yetki Hatası</h3>
                <p className="mt-1 text-sm text-red-600 dark:text-red-400">Bu sayfayı görüntüleme yetkiniz bulunmamaktadır.</p>
            </div>
        );
    }

    return (
        <div className="space-y-5">
            {/* KPI Özet Kartları */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Toplam Vardiya Grubu</p>
                        <p className="text-2xl font-extrabold text-gray-900 dark:text-white mt-1">{stats.total}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                        <Layers size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Tekli Vardiya Grupları</p>
                        <p className="text-2xl font-extrabold text-indigo-600 dark:text-indigo-400 mt-1">{stats.singleShift}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                        <Briefcase size={22} />
                    </div>
                </div>
                <div className="bg-white dark:bg-gray-800 p-4 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm flex items-center justify-between">
                    <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">Çoklu (Rotasyonlu) Havuzlar</p>
                        <p className="text-2xl font-extrabold text-emerald-600 dark:text-emerald-400 mt-1">{stats.multiShift}</p>
                    </div>
                    <div className="p-3 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400">
                        <Sparkles size={22} />
                    </div>
                </div>
            </div>

            {/* Ana Liste Kartı */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="p-5 border-b border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                            <Layers size={20} className="text-purple-500" />
                            Vardiya Grupları (Alternatif Vardiya Havuzları)
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            Çalışma periyotlarında günlere atanacak vardiya paketlerini oluşturun.
                        </p>
                    </div>

                    <div className="flex items-center gap-3">
                        <div className="relative">
                            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Grup adı ara..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-xl bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white"
                            />
                        </div>
                        {canCreate && (
                            <button
                                onClick={() => handleOpenModal()}
                                className="px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold rounded-xl flex items-center shadow-sm shadow-purple-500/20 transition-colors"
                            >
                                <PlusCircle size={17} className="mr-1.5" />
                                Yeni Grup Ekle
                            </button>
                        )}
                    </div>
                </div>

                {loading && (
                    <div className="p-12 flex justify-center items-center text-gray-500">
                        <Loader2 className="animate-spin mr-2" size={20} /> Vardiya grupları yükleniyor...
                    </div>
                )}

                {error && (
                    <div className="m-5 text-red-600 bg-red-50 dark:bg-red-900/20 p-4 rounded-xl flex items-center text-sm">
                        <AlertCircle size={18} className="mr-2" /> {error}
                    </div>
                )}

                {!loading && !error && (
                    <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-4">
                        {filteredGroups.map(group => {
                            const shifts = group.workShifts || [];
                            const isMulti = shifts.length > 1;
                            return (
                                <div
                                    key={group.id}
                                    className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/40 dark:bg-gray-900/30 p-4 hover:border-purple-300 dark:hover:border-purple-700 transition-all flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="flex items-start justify-between gap-3 mb-2">
                                            <div className="flex items-center gap-2.5">
                                                <div className={`p-2 rounded-lg ${isMulti ? 'bg-purple-100 text-purple-600 dark:bg-purple-900/40 dark:text-purple-400' : 'bg-indigo-100 text-indigo-600 dark:bg-indigo-900/40 dark:text-indigo-400'}`}>
                                                    <Layers size={18} />
                                                </div>
                                                <div>
                                                    <h3 className="font-bold text-base text-gray-900 dark:text-white">{group.name}</h3>
                                                    <span className={`inline-block text-[11px] font-semibold px-2 py-0.5 rounded-full mt-0.5 ${
                                                        isMulti
                                                            ? 'bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300'
                                                            : 'bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300'
                                                    }`}>
                                                        {isMulti ? `Rotasyonlu Havuz (${shifts.length} Vardiya)` : `Sabit Grup (${shifts.length} Vardiya)`}
                                                    </span>
                                                </div>
                                            </div>

                                            {(canUpdate || canDelete) && (
                                                <div className="flex items-center gap-1">
                                                    {canUpdate && (
                                                        <button
                                                            onClick={() => handleOpenModal(group)}
                                                            className="p-1.5 rounded-lg text-indigo-600 hover:bg-indigo-50 dark:text-indigo-400 dark:hover:bg-indigo-900/30"
                                                            title="Düzenle"
                                                        >
                                                            <Edit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(group.id)}
                                                            className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/30"
                                                            title="Sil"
                                                        >
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {group.description && (
                                            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
                                                {group.description}
                                            </p>
                                        )}
                                    </div>

                                    <div className="pt-3 border-t border-gray-200/80 dark:border-gray-700/70">
                                        <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 dark:text-gray-500 mb-2">
                                            İçerdiği Vardiyalar
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                            {shifts.map(s => (
                                                <div
                                                    key={s.id}
                                                    className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-800 dark:text-gray-200 shadow-sm"
                                                >
                                                    <Clock size={12} className="text-purple-500" />
                                                    <span className="font-semibold">{s.name}</span>
                                                    {s.startTime && s.endTime && (
                                                        <span className="text-[11px] text-gray-500 font-mono">
                                                            ({s.startTime.substring(0, 5)}-{s.endTime.substring(0, 5)})
                                                        </span>
                                                    )}
                                                </div>
                                            ))}
                                            {shifts.length === 0 && (
                                                <span className="text-xs text-amber-600 italic">Bu gruba henüz vardiya bağlanmamış</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                        {filteredGroups.length === 0 && (
                            <div className="col-span-2 text-center py-10 text-gray-500 text-sm">
                                Kriterlere uygun vardiya grubu bulunamadı.
                            </div>
                        )}
                    </div>
                )}
            </div>

            {isModalOpen && <ShiftGroupForm currentGroup={currentGroup} onSave={handleSaveSuccess} onCancel={handleCloseModal} permissions={permissions} />}

            {/* Dinamik Silme Modalı */}
            {deleteConfirmData && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-[60] p-4">
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-200 dark:border-gray-700">
                        <div className="flex items-start mb-4">
                            <div className={`flex-shrink-0 p-3 rounded-full mr-4 ${
                                deleteConfirmData.type === 'block' ? 'bg-red-100 dark:bg-red-900/30' :
                                deleteConfirmData.type === 'conflict' ? 'bg-amber-100 dark:bg-amber-900/30' :
                                'bg-red-100 dark:bg-red-900/30'
                            }`}>
                                {deleteConfirmData.type === 'block' ? (
                                    <ShieldAlert className="w-6 h-6 text-red-600 dark:text-red-400" />
                                ) : deleteConfirmData.type === 'conflict' ? (
                                    <AlertCircle className="w-6 h-6 text-amber-600 dark:text-amber-400" />
                                ) : (
                                    <Trash2 className="w-6 h-6 text-red-600 dark:text-red-400" />
                                )}
                            </div>
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">
                                    {deleteConfirmData.type === 'block' ? 'İşlem Engellendi' :
                                     deleteConfirmData.type === 'conflict' ? 'Uyarı: Bağlı Kayıtlar Bulundu' :
                                     'Silme Onayı'}
                                </h3>
                                <p className="mt-2 text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                                    {deleteConfirmData.message}
                                </p>
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3 mt-6">
                            <button
                                onClick={() => setDeleteConfirmData(null)}
                                className="px-5 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors font-medium text-sm"
                            >
                                {deleteConfirmData.type === 'block' ? 'Kapat' : 'İptal'}
                            </button>
                            {deleteConfirmData.type !== 'block' && (
                                <button
                                    onClick={() => handleDeleteConfirm(deleteConfirmData.id, deleteConfirmData.type === 'conflict')}
                                    className="px-5 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-medium text-sm flex items-center shadow-lg shadow-red-500/30"
                                >
                                    {deleteConfirmData.type === 'conflict' ? 'Onaylıyorum, Zorla Sil' : 'Evet, Sil'}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
